import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Calendar, MapPin, Plus } from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { groupService } from "../services/groupService";
import { eventService } from "../services/eventService";
import type { Group, Event } from "../types";
import { LoadingSpinner } from "../components/ui";

const isGroupLive = (group: Group): boolean => {
  if (!group.event) return false;
  const now = Date.now();
  const start = new Date(group.event.startDate).getTime();
  const end = group.event.endDate
    ? new Date(group.event.endDate).getTime()
    : start + 24 * 60 * 60 * 1000;
  return start <= now && now <= end;
};

const getEventStatusBadge = (event: Event) => {
  switch (event.status) {
    case 'PUBLISHED':
      return <span className="sf-badge-lime shrink-0">Live</span>;
    case 'PENDING':
      return (
        <span className="inline-flex shrink-0 items-center rounded-lg bg-warning-500/[.12] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-warning-400">
          Wacht op controle
        </span>
      );
    case 'REJECTED':
      return (
        <span
          className="sf-badge-pink shrink-0"
          title={event.rejectionReason || undefined}
        >
          Afgewezen
        </span>
      );
    case 'CANCELLED':
      return (
        <span className="inline-flex shrink-0 items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-body">
          Geannuleerd
        </span>
      );
    default:
      return null;
  }
};

const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [userGroups, setUserGroups] = useState<Group[]>([]);
  const [popularEvents, setPopularEvents] = useState<Event[]>([]);
  const [myEvents, setMyEvents] = useState<Event[]>([]);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        // Load user's groups, own events and popular events in parallel
        const [groupsResponse, myEventsResponse, popularResponse] = await Promise.all([
          groupService.getMyGroups({ limit: 3 }),
          eventService.getMyEvents({ limit: 5 }),
          eventService.getActiveEvents({ limit: 5 }),
        ]);

        setUserGroups(groupsResponse.groups);
        setMyEvents(myEventsResponse.events);
        setPopularEvents(popularResponse.events);

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  return (
    <div className="space-y-7">
      {/* Welcome Header */}
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <div className="sf-eyebrow mb-3">// welkom terug</div>
          <h1 className="sf-h1">
            Hey {user?.username} —{" "}
            <span className="sf-gradient-text">de nacht is jong.</span>
          </h1>
          <p className="mt-3 text-base text-fg-muted">
            Vind (of maak) een groep en ga nooit meer solo naar binnen.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2.5 sm:w-auto sm:flex-row sm:flex-wrap">
          <Link
            to="/events"
            className="sf-btn-primary animate-neon-pulse px-5 py-3.5 text-[15px]"
          >
            <Calendar className="h-[18px] w-[18px]" />
            Ontdek events
          </Link>
          <Link
            to="/groups"
            className="sf-btn-secondary px-5 py-3.5 text-[15px]"
          >
            Mijn groepen
          </Link>
          <Link
            to="/profile"
            className="sf-btn-secondary px-5 py-3.5 text-[15px]"
          >
            Profiel bewerken
          </Link>
        </div>
      </div>

      {/* Jouw evenementen */}
      <div className="sf-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-fg-bright">Jouw evenementen</h2>
          <Link
            to="/events/create"
            className="text-[13px] font-semibold text-neon-cyan transition-opacity hover:opacity-80"
          >
            Nieuw evenement
          </Link>
        </div>
        {loading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner size="sm" />
          </div>
        ) : myEvents.length > 0 ? (
          <div className="space-y-3">
            {myEvents.map((event) => (
              <div
                key={event.id}
                className="sf-card-highlight flex items-center gap-3.5 p-4"
              >
                <Link
                  to={`/events/${event.slug || event.id}`}
                  className="flex min-w-0 flex-1 items-center gap-3.5 transition-opacity hover:opacity-90"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-item bg-avatar-violet shadow-neon-pink-strong">
                    <Calendar className="h-6 w-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-bold text-fg-bright">
                      {event.title || event.name}
                    </p>
                    <p className="mt-0.5 truncate text-[13px] text-fg-muted">
                      {new Date(event.startDate).toLocaleDateString('nl-NL')}
                      {` · ${event._count?.groups ?? event.groupCount ?? 0} ${
                        (event._count?.groups ?? event.groupCount ?? 0) === 1 ? 'groep' : 'groepen'
                      }`}
                    </p>
                    {event.status === 'REJECTED' && event.rejectionReason && (
                      <p className="mt-0.5 truncate text-[12px] text-neon-pink-soft">
                        Reden: {event.rejectionReason}
                      </p>
                    )}
                  </div>
                </Link>
                {getEventStatusBadge(event)}
              </div>
            ))}
          </div>
        ) : (
          <div className="py-6 text-center">
            <p className="text-sm italic text-fg-muted">
              Nog geen evenementen aangemaakt
            </p>
            <Link
              to="/events/create"
              className="mt-2 inline-block text-[13px] font-semibold text-neon-cyan transition-opacity hover:opacity-80"
            >
              Maak je eerste evenement aan
            </Link>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Jouw groepen */}
        <div className="sf-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-fg-bright">Jouw groepen</h2>
            <Link
              to="/groups"
              className="text-[13px] font-semibold text-neon-cyan transition-opacity hover:opacity-80"
            >
              Bekijk alles
            </Link>
          </div>
          {loading ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner size="sm" />
            </div>
          ) : userGroups.length > 0 ? (
            <div className="space-y-3">
              {userGroups.map((group) => (
                <Link
                  key={group.id}
                  to={`/groups/${group.id}`}
                  className="sf-card-highlight flex items-center gap-3.5 p-4 transition-opacity hover:opacity-90"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-item bg-avatar-pink shadow-neon-pink-strong">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-bold text-fg-bright">
                      {group.name}
                    </p>
                    <p className="mt-0.5 truncate text-[13px] text-fg-muted">
                      {group.currentMembers} {group.currentMembers === 1 ? "lid" : "leden"}
                      {group.event ? ` · ${group.event.title || group.event.name}` : ""}
                    </p>
                  </div>
                  {isGroupLive(group) && (
                    <span className="sf-badge-lime shrink-0">LIVE</span>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <div className="py-6 text-center">
              <p className="text-sm italic text-fg-muted">
                Je hebt je nog niet aangesloten bij groepen
              </p>
              <Link
                to="/groups"
                className="mt-2 inline-block text-[13px] font-semibold text-neon-cyan transition-opacity hover:opacity-80"
              >
                Bekijk groepen om lid te worden
              </Link>
            </div>
          )}
          <Link
            to="/groups/create"
            className="sf-card-dashed mt-3 flex items-center gap-3 p-4 text-sm font-semibold text-fg-card transition-colors hover:border-white/25"
          >
            <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-xl bg-white/5">
              <Plus className="h-[18px] w-[18px]" />
            </div>
            Nieuwe groep aanmaken
          </Link>
        </div>

        {/* Populair nu */}
        <div className="sf-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-fg-bright">Populair nu</h2>
            <Link
              to="/events"
              className="text-[13px] font-semibold text-neon-cyan transition-opacity hover:opacity-80"
            >
              Bekijk alles
            </Link>
          </div>
          {loading ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner size="sm" />
            </div>
          ) : popularEvents.length > 0 ? (
            <div className="space-y-4">
              {popularEvents.map((event) => (
                <div
                  key={event.id}
                  className="overflow-hidden rounded-item border border-white/[.08]"
                >
                  <div className="relative flex h-[104px] items-end bg-[linear-gradient(120deg,#ff3d81,#7a3dff_50%,#23e5db)] p-3">
                    <span className="sf-badge-overlay">{event.type}</span>
                  </div>
                  <div className="p-4">
                    <p className="text-[15px] font-bold text-fg-bright">
                      {event.title || event.name}
                    </p>
                    <p className="mt-1 flex items-center gap-1.5 text-[13px] text-fg-muted">
                      <MapPin className="h-3.5 w-3.5 shrink-0 text-neon-pink" />
                      {event.location} · {new Date(event.startDate).toLocaleDateString()}
                    </p>
                    <div className="mt-3 flex gap-2">
                      <Link
                        to={`/groups?eventId=${event.id}`}
                        className="sf-btn-primary flex-1 py-2.5 text-[13px]"
                      >
                        Groepen
                      </Link>
                      <Link
                        to={`/events/${event.id}`}
                        className="sf-btn border border-white/[.14] px-4 py-2.5 text-[13px] text-fg-card hover:bg-white/5"
                      >
                        Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-6 text-center">
              <p className="text-sm italic text-fg-muted">
                Geen populaire evenementen beschikbaar
              </p>
              <Link
                to="/events"
                className="mt-2 inline-block text-[13px] font-semibold text-neon-cyan transition-opacity hover:opacity-80"
              >
                Bekijk alle evenementen
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
